import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { REGISTER_URL } from '../lib/authLinks';

const RiskFreeSection = () => {
  return (
    <section className="relative pt-20 pb-0 overflow-hidden">
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Explore Trading Section */}
        <div className="mb-16">
          <h3 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-center mb-12 leading-tight">
            Explore trading
            <br />
            <span className="text-yellow-500">with risk-free instruments</span>
          </h3>

          {/* AI Auto-Trading Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-3xl overflow-hidden border border-yellow-500/30 shadow-2xl">
              <div className="grid md:grid-cols-2 items-center">
                {/* Left: Content */}
                <div className="p-8 md:p-12 space-y-6">
                  <div className="inline-block bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2 mb-2">
                    <span className="text-yellow-500 font-bold text-sm">AI-Powered Trading</span>
                  </div>
                  
                  <h4 className="text-3xl md:text-4xl font-bold text-white">
                    AI Auto-Trading System
                  </h4>
                  
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                    Let SpreadexAsia's AI analyze the market and execute trades automatically in Forex & Stock Markets. Start trading with as little as $500.
                  </p>

                  <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 rounded-xl w-full md:w-auto transition-all duration-300 hover:scale-105 text-lg">
                      Start Now
                    </Button>
                  </a>
                </div>

                {/* Right: Design Image */}
                <div className="relative h-full min-h-[300px] overflow-hidden">
                  <img 
                    src="/images/Design 1.jpg" 
                    alt="AI Trading Design" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Subtitle */}
        <p className="text-center text-gray-400 text-base md:text-lg lg:text-xl mb-16 max-w-3xl mx-auto">
          Fully automated AI trading — no charts, no manual entries required
        </p>

        {/* Grid of Features */}
        <div className="space-y-10">
          {/* AI Market Intelligence */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1 relative min-h-[350px] sm:min-h-[400px] md:min-h-[500px] lg:min-h-[550px] bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-gray-900"></div>
              <div className="relative h-full">
                <img 
                  src="/images/Design 2.png" 
                  alt="AI Market Intelligence Design" 
                  className="w-full h-full object-contain sm:object-cover"
                />
              </div>
            </div>
            <div className="order-1 md:order-2 space-y-4 md:space-y-5 lg:space-y-6">
              <h3 className="text-3xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                AI Market Intelligence
              </h3>
              <p className="text-gray-300 text-lg md:text-lg lg:text-xl leading-relaxed">
                Real-time market scanning powered by machine learning — no signals or manual analysis required.
              </p>
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-5 md:py-5 lg:py-6 rounded-xl transition-all duration-300 hover:scale-105 text-lg md:text-lg lg:text-xl shadow-lg shadow-yellow-500/30">
                  Start Now
                </Button>
              </a>
            </div>
          </div>

          {/* Ready-to-use Trading Strategies */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-4 md:space-y-5 lg:space-y-6">
              <h3 className="text-3xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                Ready-to-use trading strategies
              </h3>
              <p className="text-gray-300 text-lg md:text-lg lg:text-xl leading-relaxed">
                <span className="text-yellow-500 font-bold text-xl md:text-xl lg:text-2xl">5–25% Daily Target Range</span>
                <br />
                Our AI aims for consistent performance with smart risk-control strategies.
              </p>
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-5 md:py-5 lg:py-6 rounded-xl transition-all duration-300 hover:scale-105 text-lg md:text-lg lg:text-xl shadow-lg shadow-yellow-500/30">
                  Get Started
                </Button>
              </a>
            </div>
            <div className="relative min-h-[450px] md:min-h-[420px] lg:min-h-[450px] bg-gradient-to-br from-yellow-900/20 via-gray-900 to-green-900/20 rounded-2xl overflow-hidden border-2 border-yellow-500/30">
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>
              <div className="absolute inset-0">
                <img 
                  src="/images/Design 3.png" 
                  alt="Trading Strategies Design" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Design Images Section */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 min-h-[320px] md:min-h-[360px]">
              <div className="h-full">
                <img 
                  src="/images/Design 4.png" 
                  alt="Design 4" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 min-h-[320px] md:min-h-[360px]">
              <div className="h-full">
                <img 
                  src="/images/Design  5.png" 
                  alt="Design 5" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Design Images - 3 Blocks */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {/* Design 6 */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 hover:border-yellow-500/50 transition-all duration-300 min-h-[280px]">
              <div className="h-full">
                <img 
                  src="/images/Design  6.png" 
                  alt="Design 6" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Design 7 */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 hover:border-yellow-500/50 transition-all duration-300 min-h-[280px]">
              <div className="h-full">
                <img 
                  src="/images/Design  7.png" 
                  alt="Design 7" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Design 8 */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 hover:border-yellow-500/50 transition-all duration-300 min-h-[280px]">
              <div className="h-full">
                <img 
                  src="/images/Design 8.png" 
                  alt="Design 8" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Licensed Broker Section */}
          <div className="rounded-2xl p-16 text-center space-y-10 max-w-5xl mx-auto mt-20 border border-gray-800">
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              SpreadexAsia is a licensed and
              <br />
              <span className="text-yellow-500">regulated online broker</span>
            </h3>
            <p className="text-gray-400 text-xl max-w-3xl mx-auto">
              Trusted by thousands of traders worldwide with secure, transparent, and compliant trading services
            </p>
            <div className="flex items-center justify-center pt-6">
              <img 
                src="/images/regulation_icons.webp" 
                alt="Licensed and Regulated - Financial Commission, €20,000 Compensation, Verify My Trade, FCA, Regulated" 
                className="max-w-full h-auto object-contain"
                style={{ maxHeight: '150px' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RiskFreeSection;
